import { useCallback, useState } from "react";

interface ModalConfig {
  title?: string;
  content?: string;
  onConfirm?: any;
  onCancel?: any;
  [key: string]: any;
}

interface ModalState {
  isOpen: boolean;
  config: ModalConfig;
}

interface Modals {
  [id: string]: ModalState;
}

function useModals() {
  const [modals, setModals] = useState<Modals>({});

  const registerModal = useCallback((id: string, config: ModalConfig = {}) => {
    setModals((prev) => ({
      ...prev,
      [id]: { isOpen: false, config },
    }));
  }, []);

  const unregisterModal = useCallback((id: string) => {
    setModals((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const openModal = (id: string) => {
    setModals((prev) => ({
      ...prev,
      [id]: { ...prev[id], isOpen: true },
    }));
  };

  const closeModal = (id: string) => {
    setModals((prev) => ({
      ...prev,
      [id]: { ...prev[id], isOpen: false },
    }));
  };

  const toggleModal = (id: string) => {
    setModals((prev) => ({
      ...prev,
      [id]: { ...prev[id], isOpen: !prev[id].isOpen },
    }));
  };

  const closeAllModals = () => {
    setModals((prev) =>
      Object.fromEntries(
        Object.entries(prev).map(([id, modal]) => [
          id,
          { ...modal, isOpen: false },
        ])
      )
    );
  };

  const isOpenModal = (id: string) => modals[id]?.isOpen || false;
  const getConfigModal = (id: string) => modals[id]?.config || {};

  return {
    isOpenModal,
    openModal,
    closeModal,
    toggleModal,
    closeAllModals,
    registerModal,
    unregisterModal,
    getConfigModal,
  };
}

export default useModals;
